import { useState, useCallback } from 'react';

/**
 * Custom hook to manage date range with a default fallback to 30 days
 * when both dates are null (reset case).
 *
 * @param initialDateRange - Initial date range to use
 * @param minDate - Optional minimum date allowed
 * @returns Object containing date range state and handler function
 */
export const useDateRangeWithDefault = (
  initialDateRange?: { from: Date | null; to: Date | null },
  minDate?: Date
) => {
  // Default to 30 days if no initial range provided
  const defaultRange = {
    from: new Date(new Date().setDate(new Date().getDate() - 30)), // 30 days ago
    to: new Date(),
  };

  // Initialize with provided range or default
  const [dateRange, setDateRange] = useState<{ from: Date | null; to: Date | null }>(
    initialDateRange || defaultRange
  );

  // Handler for date range changes
  const handleDateRangeChange = useCallback(
    (newDateRange: { from: Date | null; to: Date | null }) => {
      // If both dates are null (reset), default to 30 days
      if (newDateRange.from === null && newDateRange.to === null) {
        let defaultFrom = new Date(new Date().setDate(new Date().getDate() - 30));

        // Apply minimum date restriction if provided
        if (minDate && defaultFrom < minDate) {
          defaultFrom = new Date(minDate);
        }

        setDateRange({
          from: defaultFrom,
          to: new Date(),
        });
      } else {
        setDateRange(newDateRange);
      }
    },
    [minDate]
  );

  return {
    dateRange,
    setDateRange,
    handleDateRangeChange,
  };
};

export default useDateRangeWithDefault;
